import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import type { AssetContainer } from '@babylonjs/core/assetContainer';

import type { EntityKind } from '../../net/protocol';
import { BLEND_SECONDS, type AnimState } from './animState';
import { buildBrute, buildCaster, buildGhoul, buildImp, buildWrapped } from './beasts';
import { buildCrusader } from './crusader';
import { renderFeatures } from '../compat';
import { ModelPalette } from './palette';
import {
  advanceStride,
  attackPose,
  blendPose,
  castPose,
  deathPose,
  dodgePose,
  drawPose,
  idlePose,
  restPose,
  spinPose,
  walkPose,
  type Pose,
} from './poses';
import { applyPose, finaliseParts, type Rig } from './rig';
import { dressGear, gearSpec } from './gear';
import type { PropLibrary } from './props';
import { buildSkeleton } from './skeleton';
import {
  attachSkinnedBody,
  BODY_URLS,
  MOB_BODY_URLS,
  addMobRim,
  bodyKindFor,
  isTextured,
  loadBodyContainer,
  type BodyKind,
  type SkinnedBody,
} from './skinned';
import type { EntityModel, EntityModelFactory, PoseContext, WornGear } from './types';

/**
 * A model built from primitives and animated by the pure pose functions.
 *
 * Holds three preallocated poses — the live one, the one being blended from,
 * and a scratch target — so a frame costs no allocation at all.
 */
class ProceduralModel implements EntityModel {
  readonly root: TransformNode;
  readonly parts: readonly Mesh[];

  private readonly current: Pose = restPose();
  private readonly target: Pose = restPose();
  private readonly from: Pose = restPose();

  /** Seconds this model has existed, for the idle breath. */
  private elapsed = 0;
  /** Position within the walk cycle, advanced by distance travelled. */
  private stride = 0;
  /** Scratch for `walkUnder`: the walk cycle the legs are taken from. */
  private readonly legs = restPose();

  private state: AnimState = 'idle';
  /** Progress of the cross-fade out of `from`, 0 → 1. */
  private blend = 1;

  constructor(
    scene: Scene,
    private readonly rig: Rig,
    entityId: string,
    clickable: boolean,
    /**
     * A skinned body bound to this rig, or null for the procedural boxes.
     *
     * Optional so every existing caller and every test is unchanged, and so a body that fails
     * to load degrades to the box rig rather than to nothing.
     */
    private readonly body: SkinnedBody | null = null,
    /**
     * Dresses the body in generated gear once the rig is final (`gear.ts`), returning what it
     * placed. Run after the collider and the arm alignment, so neither sees it.
     */
    dress?: (body: SkinnedBody) => Mesh[],
  ) {
    this.root = rig.root;
    // Body and weapon together: shadows, hover outlines and metadata cover everything
    // drawn. The split exists only so `measureRig` can ignore the weapon — see rig.ts
    // — so it must not leak into what the registry treats as the model's geometry.
    // Concatenated once at construction, never per frame.
    // The skinned mesh joins `parts` — shadows, hover outline, metadata — but deliberately
    // **not** `rig.parts`, which is what `measureRig` measures. The collider stays derived from
    // the box body, so it is byte-identical whether or not a body loaded: a click target that
    // changed shape depending on whether an asset arrived is the kind of difference nothing
    // reports.
    // The returned hitbox lives under rig.root, so disposing the root disposes it.
    finaliseParts(scene, rig, entityId, clickable);
    // **After** the collider: this moves the rig's arm joints onto the body's, and the hidden box
    // arms move with them. See `SkinnedBody.alignArms`.
    body?.alignArms();
    // Generated gear hangs off the rig's joints, so the root's dispose takes it (geometry only —
    // its material is the prop library's, shared by every copy).
    const gear = body && dress ? dress(body) : [];
    for (const mesh of gear) mesh.metadata = { entityId };
    this.parts = [...rig.parts, ...rig.heldParts, ...(body ? [body.mesh] : []), ...gear];
  }

  update(dt: number, ctx: PoseContext): void {
    this.elapsed += dt;

    if (ctx.state !== this.state) {
      // Start the cross-fade from wherever the body currently is, so a state
      // change never snaps. Death and the dodge are the exceptions and take over
      // immediately: death should read as a collapse, and a roll is 0.3 s against a
      // 0.14 s blend — cross-fading it would spend half the animation at partial
      // weight and land the apex at about 55%, which is a shrug rather than a dive.
      copyPose(this.current, this.from);
      this.blend = ctx.state === 'death' || ctx.state === 'dodge' ? 1 : 0;
      this.state = ctx.state;
    }

    this.poseFor(ctx, this.target);
    this.walkUnder(dt, ctx, this.target);

    if (this.blend < 1) {
      this.blend = Math.min(1, this.blend + dt / BLEND_SECONDS);
      blendPose(this.from, this.target, this.blend, this.current);
    } else {
      copyPose(this.target, this.current);
    }

    applyPose(this.rig, this.current);
    this.glowFor(ctx);

    // **One line, and the whole skinned body depends on it.** `poses.ts` writes into the rig's
    // `TransformNode`s; without this the mesh renders in its bind pose for ever while every
    // pose test still passes, because they assert on the nodes.
    this.body?.sync();
  }

  dispose(): void {
    // The body first: its skeleton is not a child of anything, so the root's own dispose
    // cannot reach it.
    this.body?.dispose();
    // Disposing the root takes the whole hierarchy with it. Materials belong to
    // the factory and are shared, so they are left alone.
    this.root.dispose(false, false);
  }

  /**
   * **Legs that keep walking under an upper-body action.** A cast or a swing is a whole-body pose,
   * so a character who kept moving through one slid across the floor on frozen legs — reported:
   * *while casting, the legs stop moving, which is awkward as we can keep moving*. When the body
   * actually covered ground this frame, the legs, knees and the step's bob come from the walk
   * cycle and everything above the hips stays the action's. Driven by the ground covered, like the
   * walk itself, so a cast on the spot keeps its planted stance.
   */
  private walkUnder(dt: number, ctx: PoseContext, out: Pose): void {
    if (ctx.state !== 'cast' && ctx.state !== 'attack') return;
    if (dt <= 0 || ctx.distance / dt < WALK_UNDER_SPEED) return;
    this.stride = advanceStride(this.stride, ctx.distance);
    walkPose(this.stride, this.legs);
    out.legRPitch = this.legs.legRPitch;
    out.legLPitch = this.legs.legLPitch;
    out.kneeR = this.legs.kneeR;
    out.kneeL = this.legs.kneeL;
    out.torsoY = this.legs.torsoY;
  }

  private poseFor(ctx: PoseContext, out: Pose): void {
    // Only a free-handed spell lets go of the grip, and it says so itself.
    out.offHandFree = 0;
    switch (ctx.state) {
      case 'walk':
        this.stride = advanceStride(this.stride, ctx.distance);
        walkPose(this.stride, out);
        return;
      case 'attack':
        // A bow is drawn, not swung. The rig says how its weapon is held; the pose maths
        // still knows nothing about weapons.
        if (this.rig.holdKind === 'draw') drawPose(ctx.cooldown, out);
        else attackPose(ctx.cooldown, out);
        return;
      case 'cast':
        castPose(ctx.castStyle, ctx.castProgress, out);
        return;
      case 'spin':
        // The angle the body is being turned through, so the pose can bob against it rather
        // than ride round rigidly. `EntityLayer` owns the rotation itself.
        spinPose(ctx.spinPhase, out);
        return;
      case 'dodge':
        dodgePose(ctx.dodgeProgress, out);
        return;
      case 'death':
        deathPose(ctx.deathProgress, out);
        return;
      default:
        idlePose(this.elapsed, out);
    }
  }

  /**
   * Grows and shrinks the light between a caster's hands.
   *
   * Only through a cast that is not a blow — the four channelled and self-cast styles —
   * and only on a rig that built one. `scaling` is written in place and the mesh is hidden
   * at zero so a character who is not casting costs no draw call for it. The envelope is
   * the cast's own: up through the wind-up, gone by the end of the recovery, so the light
   * is brightest on the commit frame the damage lands on.
   */
  private glowFor(ctx: PoseContext): void {
    const glow = this.rig.castGlow;
    if (!glow) return;

    let s = 0;
    if (ctx.state === 'cast' && GLOWING_STYLES.has(ctx.castStyle)) {
      const p = ctx.castProgress;
      s =
        p < CAST_GLOW_PEAK
          ? p / CAST_GLOW_PEAK
          : Math.max(0, 1 - (p - CAST_GLOW_PEAK) / (1 - CAST_GLOW_PEAK));
    }
    glow.isVisible = s > 0.01;
    glow.scaling.setAll(s);
  }
}

/**
 * The cast styles that gather light in the hands: everything that is not a swing.
 *
 * A `weapon`, `hybrid`, `slam` or `sweep` cast is a blow with a weapon in it, and a glowing
 * hand on a greatsword swing would say "spell" about a thing that is not one — the same
 * reason the `attribute` pose was built with no strike component at all.
 */
const GLOWING_STYLES: ReadonlySet<string> = new Set(['attribute', 'invoke', 'utility', 'shout', 'aim']);

/** Where in a cast the gathered light is largest: the commit, when the spell goes off. */
const CAST_GLOW_PEAK = 0.4;

/**
 * How fast the body must be moving, in units a second, before an action's legs walk. Chosen: well
 * under the player's 5 and over the drift a correction or a knockback settle leaves behind.
 */
export const WALK_UNDER_SPEED = 0.8;

function copyPose(src: Pose, dst: Pose): void {
  dst.torsoY = src.torsoY;
  dst.torsoPitch = src.torsoPitch;
  dst.torsoYaw = src.torsoYaw;
  dst.headPitch = src.headPitch;
  dst.headYaw = src.headYaw;
  dst.armRPitch = src.armRPitch;
  dst.armLPitch = src.armLPitch;
  dst.armRYaw = src.armRYaw;
  dst.armLYaw = src.armLYaw;
  dst.legRPitch = src.legRPitch;
  dst.legLPitch = src.legLPitch;
  dst.elbowR = src.elbowR;
  dst.elbowL = src.elbowL;
  dst.kneeR = src.kneeR;
  dst.kneeL = src.kneeL;
  dst.weaponPitch = src.weaponPitch;
  dst.rootRoll = src.rootRoll;
  dst.rootSink = src.rootSink;
  dst.offHandFree = src.offHandFree;
}

/**
 * Which builder each catalogue mesh id maps to.
 *
 * Fewer builders than archetypes on purpose. Thirteen hand-built rigs would be
 * thirteen things to keep animating, and at the gameplay camera — a monster is
 * roughly 30 x 45 device pixels — what reads is posture, proportion and mass, not
 * species detail. Six silhouettes cover the catalogue's whole visual range; a
 * seventh is worth adding when two archetypes sharing one become confusing, not
 * before.
 *
 * An unknown id falls back to the skeleton rather than throwing: a catalogue entry
 * added ahead of its model should look wrong, not crash the render loop.
 */
const MESH_BUILDERS: Record<string, (scene: Scene, palette: ModelPalette) => Rig> = {
  mesh_skeleton_warrior: buildSkeleton,
  mesh_skeleton_archer: buildSkeleton,
  mesh_boss_skeleton_king: buildBrute,
  mesh_ghoul: buildGhoul,
  mesh_wight: buildWrapped,
  mesh_mummy: buildWrapped,
  mesh_wraith: buildCaster,
  mesh_bone_mage: buildCaster,
  mesh_dread_knight: buildBrute,
  mesh_human_cutthroat: buildGhoul,
  mesh_boss_warlord_kahn: buildBrute,
  mesh_imp: buildImp,
  mesh_boss_behemoth: buildBrute,
};

/** Every mesh id the catalogue may name, for a test to check coverage against. */
export const KNOWN_MESHES = Object.keys(MESH_BUILDERS);

/** The rig for a mesh id, or the skeleton when the id is unknown or absent. */
export function buildMob(scene: Scene, palette: ModelPalette, mesh?: string): Rig {
  const builder = mesh ? MESH_BUILDERS[mesh] : undefined;
  return (builder ?? buildSkeleton)(scene, palette);
}

/**
 * Builds models out of primitives, with no external assets.
 *
 * The one implementation of `EntityModelFactory` today. A glTF-backed factory
 * would satisfy the same interface and drop in without `entities.ts` changing —
 * that is the whole reason the interface exists.
 */
/**
 * Which body parts stay visible when a skinned body takes over.
 *
 * **The head box is the point** (owner's call): the face is a painted cell from the character
 * sheet, and the delivered `.glb` has no UVs at all — so keeping the box is what keeps a face on
 * the character. The helm and hood go with it, being headgear on the same joint.
 *
 * **The pauldrons were kept too, for one build, and it was wrong.** The reasoning was that the
 * local player's accent colour is painted on them and it is how a player finds themselves in a
 * crowd. What a browser showed is that they *float*: `rig.ts` puts a shoulder at ±0.340 because
 * that is where its box shoulder was, and the mesh's real shoulder is at ±0.203 — so a plate sized
 * for the box body hangs 14 cm off a smooth one. They are out, which is also what was actually
 * asked for (*retain the existing textured head box*).
 *
 * That leaves the local player with **no accent colour**, which is a real loss and is recorded as
 * one rather than fixed by guessing: the fix is either an accent on the body material or a pauldron
 * re-sized to the mesh, and which of those is right is a look, not a deduction.
 *
 * Everything else — chest, tabard, skirt, belt, gorget, straps, limbs — is what the mesh replaces.
 */
const SKINNED_KEEP = ['skull', 'face', 'helm', 'hood', 'cap', 'crest'];

/**
 * What a **textured** body keeps: nothing of the box rig. The skull box and its painted face cell
 * exist because the first bodies had no UVs to carry a face; a textured body has its own, and a
 * box on top of it would be two heads (Phase 28.3).
 *
 * **The headgear goes too, and that was tried the other way first.** Kept, the leather cap sat on
 * the painted hair as a brown bowl and the plate helm as a box with a crest — both sized for the
 * box skull, and a browser showed it at once. So on a textured body the head slot draws nothing
 * until helmets arrive as meshes of their own (Phase 28.1); `rig.parts` still holds them, hidden,
 * so the collider is unchanged.
 */
const TEXTURED_KEEP: readonly string[] = [];

export class ProceduralModelFactory implements EntityModelFactory {
  private readonly palette: ModelPalette;
  /** Loaded bodies, by what asks for one. Empty until `preload` resolves. */
  private readonly bodies = new Map<BodyKind, AssetContainer>();
  /** Monsters' bodies, by `mesh_id` (Phase 28.6). Empty until `preload`, and with `?mobskins=0`. */
  private readonly mobBodies = new Map<string, AssetContainer>();
  /** Where generated gear comes from; none until `useProps`, and then only what has loaded. */
  private props: PropLibrary | null = null;

  constructor(private readonly scene: Scene) {
    this.palette = new ModelPalette(scene);
  }

  /**
   * Loads the rigged bodies once, before the world is entered.
   *
   * **Fire and forget is safe and deliberate.** `create` reads `bodies` at the moment it builds,
   * so an entity that arrives before the load lands simply gets the box rig — the whole point of
   * keeping the procedural path intact. 0.4–0.9 MB each over a local connection means that window
   * is small, and nothing waits on it: a character standing in boxes for a frame is a far better
   * failure than a loading screen that can hang.
   *
   * Every body is loaded together rather than on first use, because the first vendor a player
   * walks up to — or the first plate chest equipped — should not cost a fetch mid-conversation.
   */
  /** The library the generated gear is drawn from — the zone's own, loaded once for both. */
  useProps(props: PropLibrary): void {
    this.props = props;
  }

  async preload(): Promise<void> {
    // **On by default** since 2026-09-25; `?skinned=0` returns here, the bodies are never fetched
    // (3.8 MB across the five), and `dressSkinned` finds an empty map, so every character is the
    // box rig — which is also what any single load failure degrades to.
    if (!renderFeatures.skinnedBodies) return;

    await Promise.all([
      ...(Object.keys(BODY_URLS) as BodyKind[]).map(async (kind) => {
        const container = await loadBodyContainer(this.scene, BODY_URLS[kind]);
        if (container) this.bodies.set(kind, container);
      }),
      // Monsters' too, in the same breath (2 MB): the first pack should not be boxes for a fetch.
      ...(renderFeatures.mobSkins ? Object.entries(MOB_BODY_URLS) : []).map(async ([meshId, url]) => {
        const container = await loadBodyContainer(this.scene, url);
        if (!container) return;
        addMobRim(container);
        this.mobBodies.set(meshId, container);
      }),
    ]);
  }

  create(
    kind: EntityKind,
    isSelf: boolean,
    entityId: string,
    mesh?: string,
    armour?: string,
    weapon?: string,
    gear?: WornGear,
  ): EntityModel {
    const rig =
      kind === 'mob'
        ? buildMob(this.scene, this.palette, mesh)
        : buildCrusader(this.scene, this.palette, isSelf, armour, weapon, gear);

    // **A monster wears the body for its `mesh_id`, at the mob budget** (Phase 28.6, ADR 0019).
    // Mobs were procedural-only until 2026-09-29 on a budget argument made at the hero's 12k
    // triangles; at 2,000 a painted monster is the same triangles as its boxes in one draw call
    // instead of ~20. A `mesh_id` with no body — the bosses — keeps its rig.
    const body =
      kind === 'mob'
        ? this.dressSkinned(rig, entityId, mesh === undefined ? undefined : this.mobBodies.get(mesh))
        : this.dressSkinned(rig, entityId, this.bodies.get(bodyKindFor(armour, new Set(this.bodies.keys()))));

    // Only mobs are click targets. A collider on a player would stand between
    // the cursor and the floor they are standing on.
    const props = this.props;
    const dress =
      kind !== 'mob' && body && props && isTextured(body.mesh)
        ? (b: SkinnedBody) => dressGear(rig, props, gearSpec(weapon, gear), b.alignHead(), entityId)
        : undefined;
    return new ProceduralModel(this.scene, rig, entityId, kind === 'mob', body, dress);
  }

  /**
   * Swaps the box body for the skinned one, keeping `SKINNED_KEEP`.
   *
   * The box meshes are **hidden, not disposed**, so `rig.parts` still describes the same volume
   * and `measureRig` yields the same collider it always did. Hiding costs one boolean per mesh
   * and buys an invariant that a test already asserts.
   */
  private dressSkinned(rig: Rig, entityId: string, container: AssetContainer | undefined): SkinnedBody | null {
    if (!container) return null;

    const body = attachSkinnedBody(container, rig, entityId);
    if (!body) return null;

    // **The smooth body wears whatever the box chest wore.** Taken off the chest mesh rather than
    // chosen here, so cloth, leather and plate keep reading differently without this function
    // knowing anything about armour classes — and so the local player's material choice still
    // arrives from one place. Without it the mesh gets Babylon's default material and renders as a
    // white mannequin, which is what a browser showed.
    //
    // **Unless the body brings its own** (Phase 28.3): a textured body is already painted, and the
    // chest's flat material would paint over the face, the skin and the armour alike.
    const textured = isTextured(body.mesh);
    if (!textured) {
      const chest = rig.parts.find((part) => part.name.includes('chest'));
      if (chest?.material) body.mesh.material = chest.material;
    }

    const keep = textured ? TEXTURED_KEEP : SKINNED_KEEP;
    for (const part of rig.parts) {
      if (!keep.some((name) => part.name.includes(name))) part.isVisible = false;
    }
    return body;
  }

  dispose(): void {
    this.palette.dispose();
    for (const container of [...this.bodies.values(), ...this.mobBodies.values()]) container.dispose();
    this.bodies.clear();
    this.mobBodies.clear();
  }
}
